import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { AxiosInstance } from 'axios';
import type { RootState } from './types';
import {
  getLocationAssignableStaff,
  type AssignableStaffMember,
} from '../api/employees';

type AssignableStaffEntry = {
  staff: AssignableStaffMember[];
  loading: boolean;
  featureDisabled: boolean;
  fetchedAt: number | null;
  error: string | null;
};

export interface AssignableStaffState {
  byLocationId: Record<string, AssignableStaffEntry>;
}

const EMPTY_ENTRY: AssignableStaffEntry = {
  staff: [],
  loading: false,
  featureDisabled: false,
  fetchedAt: null,
  error: null,
};

const createInitialEntry = (): AssignableStaffEntry => ({
  staff: [],
  loading: false,
  featureDisabled: false,
  fetchedAt: null,
  error: null,
});

const initialState: AssignableStaffState = {
  byLocationId: {},
};

const ensureEntry = (state: AssignableStaffState, locationId: string) => {
  if (!state.byLocationId[locationId]) {
    state.byLocationId[locationId] = createInitialEntry();
  }
  return state.byLocationId[locationId];
};

const assignableStaffSlice = createSlice({
  name: 'assignableStaff',
  initialState,
  reducers: {
    setAssignableStaffLoading: (
      state,
      action: PayloadAction<{ locationId: string; loading: boolean }>,
    ) => {
      const locationId = String(action.payload.locationId || '').trim();
      if (!locationId) return;
      const entry = ensureEntry(state, locationId);
      entry.loading = action.payload.loading;
      if (action.payload.loading) {
        entry.error = null;
      }
    },
    setAssignableStaffSuccess: (
      state,
      action: PayloadAction<{
        locationId: string;
        staff: AssignableStaffMember[];
        featureDisabled: boolean;
      }>,
    ) => {
      const locationId = String(action.payload.locationId || '').trim();
      if (!locationId) return;
      const entry = ensureEntry(state, locationId);
      entry.staff = action.payload.staff;
      entry.featureDisabled = action.payload.featureDisabled;
      entry.loading = false;
      entry.error = null;
      entry.fetchedAt = Date.now();
    },
    setAssignableStaffError: (
      state,
      action: PayloadAction<{ locationId: string; error: string }>,
    ) => {
      const locationId = String(action.payload.locationId || '').trim();
      if (!locationId) return;
      const entry = ensureEntry(state, locationId);
      entry.loading = false;
      entry.error = String(action.payload.error || 'Failed to load staff');
    },
    clearAssignableStaffForLocation: (state, action: PayloadAction<string>) => {
      const locationId = String(action.payload || '').trim();
      if (!locationId) return;
      delete state.byLocationId[locationId];
    },
    clearAllAssignableStaff: state => {
      state.byLocationId = {};
    },
  },
});

export const {
  setAssignableStaffLoading,
  setAssignableStaffSuccess,
  setAssignableStaffError,
  clearAssignableStaffForLocation,
  clearAllAssignableStaff,
} = assignableStaffSlice.actions;

export const selectAssignableStaffForLocation = (
  state: RootState,
  locationId: string,
): AssignableStaffEntry => {
  const key = String(locationId || '').trim();
  return state.assignableStaff.byLocationId[key] || EMPTY_ENTRY;
};

export const fetchAssignableStaffForLocation =
  ({
    axiosinstance,
    auth,
    locationId,
  }: {
    axiosinstance: AxiosInstance;
    auth: { accessToken?: string | null };
    locationId: string;
  }) =>
  async (dispatch: any) => {
    const key = String(locationId || '').trim();
    if (!key || !auth?.accessToken) return;

    try {
      dispatch(setAssignableStaffLoading({ locationId: key, loading: true }));
      const result = await getLocationAssignableStaff(axiosinstance, auth, key);
      dispatch(
        setAssignableStaffSuccess({
          locationId: key,
          staff: result.data,
          featureDisabled: result.status === 'disabled',
        }),
      );
    } catch (error: any) {
      dispatch(
        setAssignableStaffError({
          locationId: key,
          error: String(error?.message || 'Failed to load staff'),
        }),
      );
    }
  };

export default assignableStaffSlice.reducer;
